// src/pages/league-dashboard/CommissionerControls.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gavel, Lock, AlertCircle, Check, CheckCircle, Users, DollarSign, ChevronLeft, RefreshCw, Download } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import keeperService from '../../services/keeperService';

const CommissionerControls = ({ leagueId, league, onStatusChange }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(league?.league_status || 'setup');
  const [draftType, setDraftType] = useState(null);
  const [pricesSet, setPricesSet] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [keeperSummary, setKeeperSummary] = useState(null);
  const [keeperSummaryLoading, setKeeperSummaryLoading] = useState(false);

  // Fetch status directly from the status endpoint on mount
  useEffect(() => {
    fetchCurrentStatus();
    fetchKeeperSummary();
  }, [leagueId]);

  // Don't sync with parent league prop - it may be stale
  // Only fetch fresh data from backend

  const fetchCurrentStatus = async () => {
    try {
      // Get status directly from the status endpoint using leaguesAPI
      const statusData = await leaguesAPI.getLeagueStatus(leagueId);
      console.log('Fetched status from backend:', statusData);
      
      if (statusData.success) {
        setCurrentStatus(statusData.league_status);
        setDraftType(statusData.draft_type);
        setPricesSet(statusData.prices_set);
      }
      
      // Also check price status separately if needed
      const priceResponse = await leaguesAPI.checkPriceStatus(leagueId);
      if (priceResponse.success) {
        setPricesSet(priceResponse.prices_set);
      }
    } catch (error) {
      console.error('Error fetching current status:', error);
      setError('Failed to fetch current status');
      setTimeout(() => setError(''), 5000);
    }
  };

  const fetchKeeperSummary = async () => {
    try {
      setKeeperSummaryLoading(true);
      const response = await keeperService.getLeagueSummary(leagueId);
      if (response.success !== false) {
        setKeeperSummary(response);
      }
    } catch (err) {
      console.error('Error fetching keeper summary:', err);
    } finally {
      setKeeperSummaryLoading(false);
    }
  };

  const updateLeagueStatus = async (newStatus, isGoingBack = false) => {
    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      if (isGoingBack) {
        const confirmMessage = `Are you sure you want to go back to the ${newStatus} step? This will revert the league status.`;
        if (!window.confirm(confirmMessage)) {
          setLoading(false);
          return;
        }
      }
      
      console.log('Updating league status to:', newStatus);
      
      const response = await leaguesAPI.updateLeagueStatus(leagueId, newStatus);
      
      console.log('Update response:', response);
      
      if (response && response.success) {
        // Update local state immediately
        setCurrentStatus(newStatus);
        setRefreshKey(prev => prev + 1);
        
        // Show success message
        const successMessage = isGoingBack 
          ? `Successfully reverted to ${newStatus} step` 
          : `League status updated to: ${newStatus}`;
        setMessage(successMessage);
        
        // Clear message after 3 seconds
        setTimeout(() => {
          setMessage('');
        }, 3000);
        
        // Notify parent if exists
        if (onStatusChange) {
          try {
            onStatusChange();
          } catch (e) {
            console.error('Parent notification failed:', e);
          }
        }
        
        // Fetch fresh status after a delay to confirm backend update
        setTimeout(() => {
          fetchCurrentStatus().catch(err => {
            console.error('Status refresh failed:', err);
          });
        }, 1000);
        
      } else {
        const errorMessage = response?.message || 'Failed to update league status';
        console.error('Update failed:', errorMessage);
        setError(errorMessage);
        
        // Refresh status to ensure we're in sync
        fetchCurrentStatus();
        
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      let errorMessage = 'Failed to update league status';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      
      // Refresh status to ensure we're in sync
      fetchCurrentStatus();
      
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    const statusSteps = ['setup', 'draft', 'finalizing', 'in_season'];
    const currentIndex = statusSteps.indexOf(currentStatus);
    
    if (currentIndex > 0) {
      const previousStatus = statusSteps[currentIndex - 1];
      updateLeagueStatus(previousStatus, true);
    }
  };

  const setLeagueDraftType = async (type) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await leaguesAPI.setDraftType(leagueId, type);
      
      if (response && response.success) {
        setDraftType(type);
        await updateLeagueStatus('draft');
      } else {
        setError('Failed to set draft type');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error setting draft type:', error);
      setError('Failed to set draft type');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const startSeason = async () => {
    if (!window.confirm('Are you sure you want to start the season? This will lock rosters and prices.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await leaguesAPI.startSeason(leagueId);
      
      if (response && response.success) {
        setCurrentStatus('in_season');
        setRefreshKey(prev => prev + 1);
        setMessage('Season started successfully!');
        if (onStatusChange) onStatusChange();
        
        setTimeout(() => {
          fetchCurrentStatus();
        }, 500);
      } else {
        setError('Failed to start season');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error starting season:', error);
      setError('Failed to start season');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleEndSeason = async () => {
    if (!window.confirm(
      'End the current season? This will:\n\n' +
      '• Process year-end contracts and dead money\n' +
      '• Reset all team budgets to draft cap\n' +
      '• Transition league to Setup mode\n\n' +
      'This action cannot be undone.'
    )) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await leaguesAPI.endSeason(leagueId);

      if (response && response.success) {
        setCurrentStatus('setup');
        setRefreshKey(prev => prev + 1);
        setMessage('Season ended successfully! League is now in Setup mode.');
        if (onStatusChange) onStatusChange();

        // Force full page reload so all components pick up the new season/status
        window.location.reload();
      } else {
        setError('Failed to end season');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error ending season:', error);
      setError(error.response?.data?.detail || 'Failed to end season');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleFillLeagueToCapacity = async () => {
    if (!window.confirm('This will create empty teams to fill the league to capacity. Continue?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      const response = await leaguesAPI.fillLeagueToCapacity(leagueId);
      
      if (response && response.success) {
        setMessage(response.message || `Created ${response.teams_created} teams (${response.current_count}/${response.max_teams})`);
        setRefreshKey(prev => prev + 1);
        if (onStatusChange) onStatusChange();
        
        setTimeout(() => setMessage(''), 5000);
      } else {
        setError(response?.message || 'Failed to fill league');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error filling league:', error);
      setError(error.response?.data?.detail || 'Failed to fill league');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const [syncLoading, setSyncLoading] = useState(false);

  const handleSyncAllPlayers = async () => {
    if (!window.confirm(
      'This will import ~4,500 MLB and MiLB players (MLB, AAA, AA, High-A). ' +
      'This may take 30-60 seconds. Continue?'
    )) {
      return;
    }

    try {
      setSyncLoading(true);
      setError('');
      setMessage('');

      const response = await leaguesAPI.syncAllMLBPlayers(leagueId);

      if (response && response.success) {
        const byLevel = response.by_level || {};
        const levelDetails = Object.entries(byLevel)
          .map(([level, count]) => `${level}: ${count}`)
          .join(', ');
        setMessage(
          `Imported ${response.unique_players} unique players (${levelDetails}). ` +
          `Synced to ${response.leagues_synced} leagues.`
        );
        setTimeout(() => setMessage(''), 10000);
      } else {
        setError(response?.message || 'Failed to sync players');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error syncing players:', error);
      setError(error.response?.data?.detail || 'Failed to sync MLB/MiLB players');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSyncLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      setup: { color: 'bg-gray-500/20 text-gray-400', icon: '⚙️', label: 'Setup' },
      draft: { color: 'bg-purple-500/20 text-purple-400', icon: '🎯', label: 'Draft' },
      finalizing: { color: 'bg-blue-500/20 text-blue-400', icon: '📋', label: 'Finalizing' },
      in_season: { color: 'bg-green-500/20 text-green-400', icon: '✅', label: 'In Season' }
    };
    
    const badge = badges[status] || badges.setup;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        {badge.icon} {badge.label}
      </span>
    );
  };

  const statusSteps = ['setup', 'draft', 'finalizing', 'in_season'];
  const currentStepIndex = statusSteps.indexOf(currentStatus);
  const progressPercentage = ((currentStepIndex + 1) / statusSteps.length) * 100;
  const canGoBack = currentStepIndex > 0 && currentStatus !== 'in_season';

  return (
    <div className="space-y-6" key={refreshKey}>
      {/* Header — highlighted banner matching ManualOverrides pattern */}
      <div className={dynastyTheme.components.card.highlighted}>
        <div className="py-6 px-6">
          <div className="flex items-center gap-3">
            <Gavel className={`w-8 h-8 ${dynastyTheme.classes.text.primary}`} />
            <h1 className={`text-4xl font-bold ${dynastyTheme.classes.text.white}`}>
              Season Setup
            </h1>
          </div>
          <p className={`${dynastyTheme.classes.text.neutralLight} mt-2 max-w-3xl`}>
            Manage league state, progression, and team finances
          </p>
        </div>
      </div>

      {/* Season Setup Content — rendered directly, no tab bar */}
      <>

          {/* Status Messages */}
          {message && (
            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.success}/20 text-green-400`}>
              <Check className="inline w-5 h-5 mr-2" />
              {message}
            </div>
          )}
          
          {error && (
            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.error}/20 text-red-400`}>
              <AlertCircle className="inline w-5 h-5 mr-2" />
              {error}
            </div>
          )}

      {/* Current Status with Back Button */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className={dynastyTheme.components.heading.h2}>Current League Status</h2>
            {canGoBack && (
              <button
                onClick={handleGoBack}
                disabled={loading}
                className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
                title="Go back to previous step"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Previous Step
              </button>
            )}
          </div>
          <div className="mt-4">
            {getStatusBadge(currentStatus)}
          </div>
          
          {/* Status Timeline */}
          <div className="mt-6">
            <div className="flex items-center justify-between">
              {statusSteps.map((status, idx) => (
                <div key={status} className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    currentStepIndex >= idx
                      ? dynastyTheme.classes.bg.primary + ' text-black'
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {idx + 1}
                  </div>
                  <span className="text-xs mt-2 text-center">{status.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
            <div className="relative mt-4 -mt-8 pt-8">
              <div className="absolute top-0 left-5 right-5 h-1 bg-gray-700"></div>
              <div 
                className={`absolute top-0 left-5 h-1 ${dynastyTheme.classes.bg.primary} transition-all duration-500`}
                style={{ width: `calc(${progressPercentage}% - 2.5rem)` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Based on Status */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h2 className={dynastyTheme.components.heading.h2}>League Actions</h2>
          
          {currentStatus === 'setup' && (
            <div className="mt-4 space-y-4">
              <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
                <h3 className="font-semibold mb-2">Setup Phase</h3>
                <p className="text-sm text-gray-400 mb-4">
                  {pricesSet 
                    ? '✅ Prices have been set. You can review/adjust them or proceed to draft.' 
                    : '⚠️ You must set player prices before proceeding to draft.'}
                </p>
                <button
                  onClick={() => navigate(`/leagues/${leagueId}/salary-contract-settings`)}
                  className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'md')} mb-4 block`}
                >
                  <DollarSign className="w-4 h-4 mr-2 inline" />
                  {pricesSet ? 'Review/Adjust Prices' : 'Set Player Prices'}
                </button>
                <button
                  onClick={() => updateLeagueStatus('draft')}
                  disabled={loading || !pricesSet}
                  className={dynastyTheme.utils.getComponent('button', pricesSet ? 'primary' : 'secondary', 'md')}
                >
                  {loading ? 'Updating...' : pricesSet ? 'Proceed to Draft' : 'Prices Required First'}
                </button>
              </div>
            </div>
          )}

          {currentStatus === 'draft' && (
            <div className="mt-4 space-y-4">
              <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
                <h3 className="font-semibold mb-2">Draft Phase</h3>
                <p className="text-sm text-gray-400 mb-4">
                  Teams are building their rosters. When ready, proceed to finalize rosters.
                </p>
                <button
                  onClick={() => updateLeagueStatus('finalizing')}
                  disabled={loading}
                  className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
                >
                  {loading ? 'Updating...' : 'Move to Finalizing Phase'}
                </button>
              </div>
            </div>
          )}

          {currentStatus === 'finalizing' && (
            <div className="mt-4 space-y-4">
              <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
                <h3 className="font-semibold mb-2">Finalizing Phase</h3>
                <p className="text-sm text-gray-400 mb-4">
                  Make final roster adjustments before starting the season.
                </p>
                <button
                  onClick={startSeason}
                  disabled={loading}
                  className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
                >
                  <Lock className="w-4 h-4 mr-2 inline" />
                  {loading ? 'Starting...' : 'Start Season'}
                </button>
                <p className="text-xs text-red-400 mt-2">
                  ⚠️ This will activate the season and lock prices.
                </p>
              </div>
            </div>
          )}

          {currentStatus === 'in_season' && (
            <div className="mt-4 space-y-4">
              <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <h3 className={dynastyTheme.components.heading.h2}>Season Active</h3>
                </div>
                <p className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-4`}>
                  End the current season to begin offseason setup. This will process year-end contracts,
                  create dead money obligations, reset team budgets, and transition the league to setup mode.
                </p>
                <button
                  onClick={handleEndSeason}
                  disabled={loading}
                  className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
                >
                  {loading ? 'Processing...' : 'End Season'}
                </button>
                <p className="text-xs text-red-400 mt-2">
                  This action cannot be undone.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Keeper Submission Status */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className={dynastyTheme.components.heading.h2}>
                Keeper Submissions {keeperSummary?.season ? `for ${keeperSummary.season}` : ''}
              </h2>
              {keeperSummary && (
                <p className={`text-sm ${dynastyTheme.classes.text.neutralLight} mt-1`}>
                  {keeperSummary.teams_ready}/{keeperSummary.total_teams} submitted
                  {keeperSummary.team_summaries?.filter(t => !t.is_ready && t.saved > 0).length > 0 && (
                    <span className="ml-2 text-yellow-400">{keeperSummary.team_summaries.filter(t => !t.is_ready && t.saved > 0).length} draft saved</span>
                  )}
                  {keeperSummary.all_teams_ready && <span className="ml-2 text-emerald-400 font-semibold">All submitted</span>}
                </p>
              )}
            </div>
            <button
              onClick={fetchKeeperSummary}
              disabled={keeperSummaryLoading}
              className={dynastyTheme.utils.getComponent('button', 'ghost', 'sm')}
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${keeperSummaryLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {keeperSummaryLoading && !keeperSummary && (
            <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>Loading...</p>
          )}

          {keeperSummary?.team_summaries?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${dynastyTheme.classes.border.neutral}`}>
                    <th className={`text-left py-2 pr-4 font-semibold ${dynastyTheme.classes.text.neutralLight}`}>Team</th>
                    <th className={`text-center py-2 px-4 font-semibold ${dynastyTheme.classes.text.neutralLight}`}>Keepers</th>
                    <th className={`text-left py-2 px-4 font-semibold ${dynastyTheme.classes.text.neutralLight}`}>Status</th>
                    <th className={`text-left py-2 pl-4 font-semibold ${dynastyTheme.classes.text.neutralLight}`}>Submitted At</th>
                  </tr>
                </thead>
                <tbody>
                  {keeperSummary.team_summaries.map(team => (
                    <tr key={team.team_id} className={`border-b ${dynastyTheme.classes.border.neutral} last:border-0`}>
                      <td className={`py-2 pr-4 font-medium ${dynastyTheme.classes.text.white}`}>
                        {team.team_name}
                      </td>
                      <td className={`py-2 px-4 text-center font-mono ${dynastyTheme.classes.text.white}`}>
                        {team.final}/{keeperSummary.num_keepers ?? '?'}
                        {team.saved > 0 && (
                          <span className={`ml-1 text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                            (+{team.saved} draft)
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-4">
                        {team.is_ready ? (
                          <span className="text-emerald-400 font-semibold">Submitted</span>
                        ) : team.saved > 0 ? (
                          <span className="text-yellow-400 font-semibold">Draft Saved</span>
                        ) : (
                          <span className="text-red-400 font-semibold">Not Started</span>
                        )}
                      </td>
                      <td className={`py-2 pl-4 text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                        {team.finalized_at
                          ? new Date(team.finalized_at).toLocaleString()
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {keeperSummary && keeperSummary.team_summaries?.length === 0 && (
            <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>No keeper selections found.</p>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h2 className={dynastyTheme.components.heading.h2}>Quick Actions</h2>
          <div className="grid grid-cols-1 gap-4 mt-4">
            <button
              onClick={() => navigate(`/leagues/${leagueId}/salary-contract-settings`)}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
            >
              <DollarSign className="w-4 h-4 mr-2 inline" />
              Manage Prices
            </button>
            <button
              onClick={handleFillLeagueToCapacity}
              disabled={loading}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
            >
              <Users className="w-4 h-4 mr-2 inline" />
              {loading ? 'Filling...' : 'Fill League to Capacity'}
            </button>
            <button
              onClick={handleSyncAllPlayers}
              disabled={syncLoading}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
            >
              <Download className="w-4 h-4 mr-2 inline" />
              {syncLoading ? 'Importing Players...' : 'Sync All MLB/MiLB Players'}
            </button>
          </div>
        </div>
      </div>
      </>

    </div>
  );
};

export default CommissionerControls;